import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Typography,
  Divider,
  Tooltip,
  Collapse,
  ButtonBase,
} from '@mui/material';
import {
  CheckCircle as TaughtIcon,
  RadioButtonUnchecked as UntaughtIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import { groupAndSortTopics } from '../../utils/topicListSort';

export interface TopicListItem {
  nodeId: number;
  label: string;
  scheduledDate?: string | null;
  isTaught: boolean;
}

interface TopicListPanelProps {
  open: boolean;
  topics: TopicListItem[];
  selectedNodeId: number | null;
  onTopicSelect: (nodeId: number) => void;
  onToggleTaught?: (nodeId: number) => void;
  drawerWidth?: number;
}

const TopicListPanel: React.FC<TopicListPanelProps> = ({
  open,
  topics,
  selectedNodeId,
  onTopicSelect,
  onToggleTaught,
  drawerWidth = 280,
}) => {
  const [showCompleted, setShowCompleted] = useState(false);
  const { completed, uncovered } = groupAndSortTopics(topics);

  const renderTopicItem = (topic: TopicListItem, muted: boolean) => (
    <ListItemButton
      key={topic.nodeId}
      selected={topic.nodeId === selectedNodeId}
      onClick={() => onTopicSelect(topic.nodeId)}
      sx={{ py: 1.5, px: 2, opacity: muted ? 0.6 : 1 }}
    >
      <ListItemText
        primary={
          <Typography
            variant="body2"
            fontWeight={topic.nodeId === selectedNodeId ? 600 : 400}
            sx={{ textDecoration: muted ? 'line-through' : 'none' }}
            noWrap
          >
            {topic.label}
          </Typography>
        }
        secondary={
          <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
            {topic.scheduledDate && (
              <Chip
                label={topic.scheduledDate}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
            <Tooltip title={onToggleTaught ? (topic.isTaught ? 'Mark as not taught' : 'Mark as taught') : ''}>
              <Chip
                icon={topic.isTaught ? <TaughtIcon sx={{ fontSize: '12px !important' }} /> : <UntaughtIcon sx={{ fontSize: '12px !important' }} />}
                label={topic.isTaught ? 'Taught' : 'Not Yet'}
                size="small"
                color={topic.isTaught ? 'success' : 'default'}
                variant={topic.isTaught ? 'filled' : 'outlined'}
                onClick={onToggleTaught ? (e) => { e.stopPropagation(); onToggleTaught(topic.nodeId); } : undefined}
                clickable={!!onToggleTaught}
                sx={{ height: 18, fontSize: '0.65rem', cursor: onToggleTaught ? 'pointer' : 'default' }}
              />
            </Tooltip>
          </Box>
        }
      />
    </ListItemButton>
  );

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      sx={{
        width: open ? drawerWidth : 0,
        flexShrink: 0,
        transition: 'width 0.2s',
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          position: 'relative',
          height: '100%',
          overflowY: 'auto',
          border: 'none',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
          TOPICS
        </Typography>
      </Box>
      <Divider />

      {completed.length > 0 && (
        <>
          <ButtonBase
            onClick={() => setShowCompleted((v) => !v)}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
              textAlign: 'left',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {completed.length} topic{completed.length === 1 ? '' : 's'} completed
            </Typography>
            {showCompleted ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
          </ButtonBase>
          <Collapse in={showCompleted}>
            <List dense disablePadding>
              {completed.map((topic) => renderTopicItem(topic, true))}
            </List>
          </Collapse>
          <Divider />
        </>
      )}

      <List dense disablePadding>
        {uncovered.map((topic) => renderTopicItem(topic, false))}
        {topics.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No topics in this course.
            </Typography>
          </Box>
        )}
        {topics.length > 0 && uncovered.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              All topics covered — nice work!
            </Typography>
          </Box>
        )}
      </List>
    </Drawer>
  );
};

export default TopicListPanel;
